/**
 * O chip do comando (`/quem-migrar`, `/crm`) na área de transferência.
 *
 * O chip é IRMÃO do `<textarea>`, então Ctrl+A/Ctrl+C nativos levavam só o
 * texto. Aqui:
 * - copiar/recortar uma seleção que começa no início do campo escreve
 *   `<comando> <trecho>` — o que o usuário vê na caixa;
 * - recortar TUDO (Ctrl+A, Ctrl+X) leva o chip junto e esvazia a caixa: é o
 *   "apagar a mensagem inteira" que o gesto significa; recorte parcial só tira o
 *   trecho e o chip fica;
 * - com a seleção cobrindo o texto inteiro, `chipNaSelecao` acende o chip;
 * - colar `/quem-migrar sasa` numa caixa vazia e sem chip reconstitui o chip.
 *
 * Contrato do host: `value`, `chipDoComposer`, `atalhoSelecionado`,
 * `limparAtalhoSelecionado`, `atalhos`, `ferramentas`, `elementoDoCampo()` e o
 * listener `executar-atalho` (mixins de atalhos e de altura + `send-message.vue`).
 */
import { separarComandoDaFala } from '@modules/aawz-intelligence-v2/utils/comando-na-fala'

const TIPO_TEXTO = 'text/plain'

export const sendMessageChipClipboardMixin = {
  data() {
    return {
      chipNaSelecao: false,
      // Caixa VAZIA com chip: não há texto para o browser selecionar, então o
      // "selecionar tudo" (Ctrl+A ou arrastar a partir do chip) é marcado aqui.
      chipSelecionadoSemTexto: false,
    }
  },
  mounted() {
    document.addEventListener('selectionchange', this.atualizarChipNaSelecao)
  },
  beforeDestroy() {
    document.removeEventListener('selectionchange', this.atualizarChipNaSelecao)
  },
  watch: {
    value() {
      this.chipSelecionadoSemTexto = false
      this.atualizarChipNaSelecao()
    },
    chipDoComposer() {
      this.atualizarChipNaSelecao()
    },
  },
  methods: {
    // Ctrl/Cmd+A com chip: seleciona o texto (nativo) e acende o chip — mesmo
    // com a caixa vazia, onde não há seleção nenhuma para o browser fazer.
    tratarSelecionarTudoComChip(event) {
      const ehSelecionarTudo = (event.ctrlKey || event.metaKey) && !event.altKey &&
        String(event.key).toLowerCase() === 'a'
      if (!ehSelecionarTudo || !this.chipDoComposer) return
      this.selecionarTudoComChip()
    },
    selecionarTudoComChip() {
      const campo = this.elementoDoCampo()
      if (!campo) return
      campo.focus()
      campo.setSelectionRange(0, String(this.value || '').length)
      this.chipSelecionadoSemTexto = true
      this.atualizarChipNaSelecao()
    },
    // Ctrl/Cmd+C com a caixa vazia: o browser não dispara `copy` sem seleção.
    tratarCopiarChipSemTexto(event) {
      const ehCopiar = (event.ctrlKey || event.metaKey) &&
        String(event.key).toLowerCase() === 'c'
      if (!ehCopiar || !this.chipDoComposer || this.value) return
      if (!this.chipNaSelecao || !navigator.clipboard) return
      navigator.clipboard.writeText(this.textoComChip('')).catch(function () {})
    },
    // `null` quando a cópia é a nativa: sem chip, ou seleção fora do início.
    selecaoComChip() {
      if (!this.chipDoComposer) return null
      const campo = this.elementoDoCampo()
      if (!campo || campo.selectionStart !== 0) return null
      const valor = String(this.value || '')
      const fim = campo.selectionEnd
      if (fim === 0 && valor) return null
      return { fim: fim, total: fim >= valor.length, trecho: valor.slice(0, fim) }
    },
    textoComChip(trecho) {
      const comando = this.chipDoComposer.rotulo
      return trecho ? comando + ' ' + trecho : comando
    },
    aoCopiarComChip(event) {
      const selecao = this.selecaoComChip()
      if (!selecao || !event.clipboardData) return
      event.clipboardData.setData(TIPO_TEXTO, this.textoComChip(selecao.trecho))
      event.preventDefault()
    },
    aoRecortarComChip(event) {
      const selecao = this.selecaoComChip()
      if (!selecao || !event.clipboardData) return
      event.clipboardData.setData(TIPO_TEXTO, this.textoComChip(selecao.trecho))
      event.preventDefault()
      this.$emit('input', String(this.value || '').slice(selecao.fim))
      if (selecao.total) this.removerChipDoComposer()
    },
    removerChipDoComposer() {
      if (this.atalhoSelecionado) return this.limparAtalhoSelecionado()
      this.$emit('limpar-ferramenta')
    },
    atualizarChipNaSelecao() {
      const campo = this.chipDoComposer ? this.elementoDoCampo() : null
      const valor = String(this.value || '')
      const cobreTudo = Boolean(
        campo &&
          document.activeElement === campo &&
          campo.selectionStart === 0 &&
          campo.selectionEnd >= valor.length
      )
      // Sem texto, só o gesto explícito acende — senão o chip ficaria aceso
      // sempre que o campo vazio tivesse o foco.
      this.chipNaSelecao = valor ? cobreTudo : Boolean(cobreTudo && this.chipSelecionadoSemTexto)
    },
    // Colar `/comando resto` numa caixa vazia e sem chip: chip + resto.
    aoColarComando(event) {
      if (this.value || this.chipDoComposer || !event.clipboardData) return
      const colado = event.clipboardData.getData(TIPO_TEXTO)
      const alvo = this.comandoDoTextoColado(colado)
      if (!alvo) return
      event.preventDefault()
      alvo.aplicar()
      this.$emit('input', alvo.resto)
    },
    // Atalho só conta com host escutando `executar-atalho`; módulo vale sempre.
    comandoDoTextoColado(texto) {
      const temAtalhos = Boolean(this.$listeners['executar-atalho'])
      const comando = separarComandoDaFala(texto, temAtalhos ? this.atalhos : [], this.ferramentas)
      if (!comando) return null
      const aplicar = comando.atalho
        ? () => {
            this.atalhoSelecionado = comando.atalho
          }
        : () => this.$emit('escolher-ferramenta', comando.modulo)
      return { resto: comando.argumento, aplicar: aplicar }
    },
  },
}
